import type { Dispatch, ReactNode, SetStateAction } from "react";
import type { FloorSellableItem } from "../../api/shopMode";
import { useT } from "../../i18n/LanguageProvider";
import { t } from "../../i18n/t";

/** One stock item on a floor ticket (inline sell, kiosk order form). */
export type TicketLine = {
  item: FloorSellableItem;
  qtyStr: string;
  priceStr: string;
};

/**
 * Why a ticket cannot be submitted as shown — or null when it can. The qty
 * rule mirrors the till's (never blank/zero, never above stock): a blanked
 * qty used to be DISPLAYED as €0 but SUBMITTED as quantity 1 (code-review
 * 2026-08-09), so validation lives here, once, for every host.
 * `requirePrices` is off for the kiosk order form, where an unpriced line
 * legitimately means "quote it later".
 */
export function ticketLinesProblem(
  lines: TicketLine[],
  opts: { requirePrices: boolean },
): string | null {
  for (const line of lines) {
    const qty = Number(line.qtyStr);
    if (line.qtyStr.trim() === "" || !Number.isFinite(qty) || qty <= 0) {
      return t("ticket.setQuantity", { name: line.item.name });
    }
    const available = Number(line.item.qtyOnHand);
    if (Number.isFinite(available) && qty > available) {
      return t("ticket.onlyInStock", { qty: line.item.qtyOnHand, name: line.item.name });
    }
    if (
      opts.requirePrices &&
      (line.priceStr.trim() === "" || !Number.isFinite(Number(line.priceStr)))
    ) {
      return t("ticket.setPrice", { name: line.item.name });
    }
  }
  return null;
}

export function ticketLinesTotal(lines: TicketLine[]): number {
  return lines.reduce(
    (acc, l) => acc + (Number(l.qtyStr) || 0) * (Number(l.priceStr) || 0),
    0,
  );
}

/**
 * The editable qty/price rows of a floor ticket — ONE implementation for the
 * inline sell and the kiosk order form (code-review 2026-08-09: the two
 * copies were born identical and would only ever drift apart).
 */
export function TicketLines({
  lines,
  onLinesChange,
  disabled,
  footer,
}: {
  lines: TicketLine[];
  onLinesChange: Dispatch<SetStateAction<TicketLine[]>>;
  disabled?: boolean;
  /** Optional trailing row (the order form's running-total hint). */
  footer?: ReactNode;
}) {
  const { t } = useT();
  if (lines.length === 0) return null;
  return (
    <div className="sf-inline-sell-ticket">
      {lines.map((line) => (
        <div key={line.item.id} className="sf-inline-sell-line">
          <span className="sf-inline-sell-line-name">{line.item.name}</span>
          <input
            type="number"
            inputMode="numeric"
            min="0"
            aria-label={t("ticket.quantityOf", { name: line.item.name })}
            value={line.qtyStr}
            onChange={(e) =>
              onLinesChange((cur) =>
                cur.map((l) =>
                  l.item.id === line.item.id ? { ...l, qtyStr: e.target.value } : l,
                ),
              )
            }
            /**
             * Owner ruling 2026-09-10: a quantity of zero removes the line, so
             * the same gesture works here and at the till. Judged on BLUR, not
             * per keystroke — "0" is a legitimate step towards "0.5".
             */
            onBlur={() => {
              const qty = Number(line.qtyStr);
              if (line.qtyStr.trim() === "" || !Number.isFinite(qty) || qty <= 0) {
                onLinesChange((cur) => cur.filter((l) => l.item.id !== line.item.id));
              }
            }}
            disabled={disabled}
          />
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            aria-label={t("ticket.unitPriceOf", { name: line.item.name })}
            value={line.priceStr}
            onChange={(e) =>
              onLinesChange((cur) =>
                cur.map((l) =>
                  l.item.id === line.item.id ? { ...l, priceStr: e.target.value } : l,
                ),
              )
            }
            disabled={disabled}
          />
          <button
            type="button"
            className="button plain"
            aria-label={t("ticket.remove", { name: line.item.name })}
            onClick={() =>
              onLinesChange((cur) => cur.filter((l) => l.item.id !== line.item.id))
            }
            disabled={disabled}
          >
            ×
          </button>
        </div>
      ))}
      {footer}
    </div>
  );
}
